// Stocky の Low Stock CSV から発注点・仕入先別リードタイムを一括取り込みする
// モーダル。列名はエクスポート種別で揺れるため、①ファイル選択 → ②列の対応付け
// （サーバの推測を初期値に）→ ③上書き件数の確認 → ④取り込み結果、の4段で進む。
//
// ③が要るのは、Stocky から移ってきた店が既に手で発注点を入れている可能性が
// あり、値の入っている列は取り込みで上書きされるため。件数はサーバの dry-run
// （apply と同じロジック）から取るので、表示と実際の結果がドリフトしない。
import {
  Banner,
  BlockStack,
  Checkbox,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import {
  applyReorderCsv,
  fileToBase64,
  previewReorderCsv,
  type CsvImportPreview,
  type CsvImportMapping,
  type CsvImportResult,
} from "../api/reorder_policy_import";

import { t } from "../lib/i18n";

const UNUSED_VALUE = "";

interface MappingState {
  sku: string;
  supplier_name: string;
  reorder_point: string;
  lead_time_days: string;
}

const EMPTY_MAPPING: MappingState = {
  sku: UNUSED_VALUE,
  supplier_name: UNUSED_VALUE,
  reorder_point: UNUSED_VALUE,
  lead_time_days: UNUSED_VALUE,
};

function overwriteCount(result: CsvImportResult): number {
  return result.updated + result.lead_times_updated;
}

function writtenCount(result: CsvImportResult): number {
  return (
    result.created +
    result.updated +
    result.lead_times_created +
    result.lead_times_updated +
    result.preferred_repaired
  );
}

function listed(values: readonly string[]): string {
  return values.slice(0, 10).join(", ") + (values.length > 10 ? " …" : "");
}

interface ImportSnapshot {
  revision: number;
  fileBase64: string;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
  mapping: CsvImportMapping;
}

interface PendingConfirmation {
  result: CsvImportResult;
  snapshot: ImportSnapshot;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 取り込み成功後に呼ばれる（親が一覧クエリを invalidate する）。 */
  onImported: () => void;
}

export function ReorderCsvImportModal({ open, onClose, onImported }: Props) {
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [sourceImportId, setSourceImportId] = useState("");
  const [sourceMediaType, setSourceMediaType] = useState("text/csv");
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const [mapping, setMapping] = useState<MappingState>(EMPTY_MAPPING);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  /** dry-run の結果。null 以外＝上書き確認の待ち状態。 */
  const [pendingConfirm, setPendingConfirm] =
    useState<PendingConfirmation | null>(null);
  const [retentionOnlyConfirmed, setRetentionOnlyConfirmed] = useState(false);
  const inputRevision = useRef(0);

  const previewMutation = useMutation({
    mutationFn: (variables: { fileBase64: string; revision: number }) =>
      previewReorderCsv(variables.fileBase64),
    onSuccess: (data, variables) => {
      if (variables.revision !== inputRevision.current) return;
      setPreview(data);
      setResult(null);
      setPendingConfirm(null);
      setRetentionOnlyConfirmed(false);
      setMapping({
        sku: data.guessed_mapping.sku ?? UNUSED_VALUE,
        supplier_name: data.guessed_mapping.supplier_name ?? UNUSED_VALUE,
        reorder_point: data.guessed_mapping.reorder_point ?? UNUSED_VALUE,
        lead_time_days: data.guessed_mapping.lead_time_days ?? UNUSED_VALUE,
      });
    },
  });

  const applyMutation = useMutation({
    mutationFn: (variables: {
      dryRun: boolean;
      snapshot: ImportSnapshot;
      expectedPlanToken?: string;
      confirmRetentionOnly?: boolean;
    }) =>
      applyReorderCsv(
        variables.snapshot.fileBase64,
        variables.snapshot.mapping,
        {
          dryRun: variables.dryRun,
          expectedPlanToken: variables.expectedPlanToken,
          confirmRetentionOnly: variables.confirmRetentionOnly,
          sourceImportId: variables.snapshot.sourceImportId,
          sourceFileName: variables.snapshot.sourceFileName,
          sourceMediaType: variables.snapshot.sourceMediaType,
        },
      ),
    onSuccess: (data, variables) => {
      if (variables.snapshot.revision !== inputRevision.current) return;
      if (data.dry_run !== variables.dryRun) return;
      if (variables.dryRun) {
        setRetentionOnlyConfirmed(false);
        setPendingConfirm({ result: data, snapshot: variables.snapshot });
        return;
      }
      setPendingConfirm(null);
      setResult(data);
      if (writtenCount(data) > 0) onImported();
    },
  });

  /** 対応付けを変えたら確認済みの件数は無効（別の対応付けの件数を見せない）。 */
  const updateMapping = useCallback((patch: Partial<MappingState>) => {
    inputRevision.current += 1;
    setSourceImportId(globalThis.crypto.randomUUID());
    setPendingConfirm(null);
    setRetentionOnlyConfirmed(false);
    setResult(null);
    setMapping((current) => ({ ...current, ...patch }));
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const revision = inputRevision.current + 1;
      inputRevision.current = revision;
      setFileName(file.name);
      setSourceImportId(globalThis.crypto.randomUUID());
      setSourceMediaType(file.type || "text/csv");
      setFileBase64(null);
      setPreview(null);
      setMapping(EMPTY_MAPPING);
      setResult(null);
      setPendingConfirm(null);
      setRetentionOnlyConfirmed(false);
      const base64 = await fileToBase64(file);
      if (revision !== inputRevision.current) return;
      setFileBase64(base64);
      previewMutation.mutate({ fileBase64: base64, revision });
    },
    [previewMutation],
  );

  const handleClose = useCallback(() => {
    inputRevision.current += 1;
    setFileBase64(null);
    setFileName("");
    setSourceImportId("");
    setPreview(null);
    setMapping(EMPTY_MAPPING);
    setResult(null);
    setPendingConfirm(null);
    setRetentionOnlyConfirmed(false);
    previewMutation.reset();
    applyMutation.reset();
    onClose();
  }, [applyMutation, onClose, previewMutation]);

  const headerOptions = [
    { label: t("（使わない）"), value: UNUSED_VALUE },
    ...(preview?.headers ?? []).map((header) => ({
      label: header,
      value: header,
    })),
  ];
  const hasValueColumn =
    mapping.reorder_point !== UNUSED_VALUE ||
    mapping.lead_time_days !== UNUSED_VALUE;
  const hasSupplierForLeadTime =
    mapping.lead_time_days === UNUSED_VALUE ||
    mapping.supplier_name !== UNUSED_VALUE;
  const isBusy = previewMutation.isPending || applyMutation.isPending;
  const canApply =
    preview !== null &&
    fileBase64 !== null &&
    mapping.sku !== UNUSED_VALUE &&
    hasValueColumn &&
    hasSupplierForLeadTime &&
    !isBusy;
  const isConfirming =
    pendingConfirm !== null &&
    pendingConfirm.snapshot.revision === inputRevision.current;
  const pendingResult = isConfirming ? pendingConfirm.result : null;
  const pendingRetentionOnly =
    pendingResult?.normalization_mode === "retention_only";

  const handleApply = useCallback(() => {
    if (isBusy) return;
    if (pendingConfirm !== null) {
      if (pendingConfirm.snapshot.revision !== inputRevision.current) {
        setPendingConfirm(null);
        return;
      }
      if (
        pendingConfirm.result.normalization_mode === "retention_only" &&
        !retentionOnlyConfirmed
      ) {
        return;
      }
      applyMutation.mutate({
        dryRun: false,
        snapshot: pendingConfirm.snapshot,
        expectedPlanToken: pendingConfirm.result.plan_token ?? undefined,
        confirmRetentionOnly:
          pendingConfirm.result.normalization_mode === "retention_only",
      });
      return;
    }
    if (!fileBase64 || !sourceImportId || !canApply) return;
    applyMutation.mutate({
      dryRun: true,
      snapshot: {
        revision: inputRevision.current,
        fileBase64,
        sourceImportId,
        sourceFileName: fileName,
        sourceMediaType,
        mapping: {
          sku: mapping.sku,
          supplier_name: mapping.supplier_name || null,
          reorder_point: mapping.reorder_point || null,
          lead_time_days: mapping.lead_time_days || null,
        },
      },
    });
  }, [
    applyMutation,
    canApply,
    fileBase64,
    fileName,
    isBusy,
    mapping,
    pendingConfirm,
    retentionOnlyConfirmed,
    sourceImportId,
    sourceMediaType,
  ]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("StockyのLow Stock CSVを取り込む")}
      primaryAction={{
        content: pendingRetentionOnly
          ? t("原本と汎用JSONLを保存する")
          : isConfirming
            ? "上書きして取り込む"
            : t("取り込む"),
        onAction: handleApply,
        disabled:
          !canApply ||
          applyMutation.isPending ||
          (pendingRetentionOnly && !retentionOnlyConfirmed),
        loading: applyMutation.isPending,
        destructive:
          !pendingRetentionOnly &&
          pendingResult !== null &&
          overwriteCount(pendingResult) > 0,
      }}
      secondaryActions={[{ content: t("閉じる"), onAction: handleClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Banner tone="info" title={t("保存済みLow Stock CSVを取り込みます")}>
            <p>
              {t(
                "終了前に保存した low_stock_csv.csv を選んでください。使う列は SKU・Vendor・ROP（発注点）・Lead Time の4つです。",
              )}
            </p>
          </Banner>

          <input
            type="file"
            accept=".csv,text/csv"
            aria-label={t("CSVファイル")}
            disabled={isBusy}
            onChange={(event) => void handleFileChange(event)}
          />
          {fileName && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t("選択中: {name}", { name: fileName })}
            </Text>
          )}

          {previewMutation.isError && (
            <Banner tone="critical" title={t("CSV を読み込めませんでした")}>
              <p>{(previewMutation.error as Error).message}</p>
            </Banner>
          )}

          {preview && (
            <BlockStack gap="300">
              <Text as="p">
                {t("{n} 行を検出しました。CSV", { n: preview.total_rows })}
                {t(
                  "の列と取り込み先を対応付けてください（推測済みの列は確認だけで進めます）。",
                )}
              </Text>
              <Select
                label={t("SKU の列（必須）")}
                options={headerOptions}
                value={mapping.sku}
                onChange={(value) => updateMapping({ sku: value })}
                disabled={isBusy}
              />
              <Select
                label={t("仕入先（Vendor）の列")}
                options={headerOptions}
                value={mapping.supplier_name}
                onChange={(value) => updateMapping({ supplier_name: value })}
                disabled={isBusy}
              />
              <Select
                label={t("発注点の列")}
                options={headerOptions}
                value={mapping.reorder_point}
                onChange={(value) => updateMapping({ reorder_point: value })}
                disabled={isBusy}
              />
              <Select
                label={t("リードタイム（日数）の列")}
                options={headerOptions}
                value={mapping.lead_time_days}
                onChange={(value) => updateMapping({ lead_time_days: value })}
                disabled={isBusy}
              />
              {!canApply && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "SKU の列と、発注点またはリードタイムの列を選んでください。リードタイムを選ぶ場合は仕入先（Vendor）の列も必要です。",
                  )}
                </Text>
              )}
            </BlockStack>
          )}

          {pendingResult && pendingRetentionOnly && (
            <Banner
              tone="warning"
              title={t(
                "100万物理行を超えるため、運用データへは反映せず保全します",
              )}
            >
              <BlockStack gap="200">
                <Text as="p">
                  {t(
                    "このファイルは {n} 行あります。発注点・リードタイムには1件も書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保持します。",
                    { n: pendingResult.total_rows },
                  )}
                </Text>
                <Text as="p">
                  {t(
                    "保存後は「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。まだ何も保存していません。",
                  )}
                </Text>
                <Checkbox
                  label={t(
                    "発注点・リードタイムへ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する",
                  )}
                  checked={retentionOnlyConfirmed}
                  onChange={setRetentionOnlyConfirmed}
                  disabled={isBusy}
                />
              </BlockStack>
            </Banner>
          )}

          {pendingResult && !pendingRetentionOnly && (
            <Banner
              tone={overwriteCount(pendingResult) > 0 ? "warning" : "info"}
              title={
                overwriteCount(pendingResult) > 0
                  ? t("既存の設定 {n} 件を上書きします", {
                      n: overwriteCount(pendingResult),
                    })
                  : t("上書きされる既存の設定はありません")
              }
            >
              <BlockStack gap="200">
                <Text as="p">
                  {t(
                    "発注点は新規 {created} 件・上書き {updated} 件、仕入先別リードタイムは新規 {leadCreated} 件・上書き {leadUpdated} 件です。よろしければ「上書きして取り込む」を",
                    {
                      created: pendingResult.created,
                      updated: pendingResult.updated,
                      leadCreated: pendingResult.lead_times_created,
                      leadUpdated: pendingResult.lead_times_updated,
                    },
                  )}
                  {t("押してください（まだ何も保存していません）。")}
                </Text>
                <Text as="p">
                  {t(
                    "変更なし: 発注点 {unchanged} 件・リードタイム {leadUnchanged} 件 / 既定仕入先の整合修復 {preferredRepaired} 件",
                    {
                      unchanged: pendingResult.unchanged,
                      leadUnchanged: pendingResult.lead_times_unchanged,
                      preferredRepaired: pendingResult.preferred_repaired,
                    },
                  )}
                </Text>
                {overwriteCount(pendingResult) > 0 && (
                  <Text as="p">
                    {t(
                      "上書きされるのは対応付けた列だけです（空欄のセル・「（使わない）」にした列の既存値は残ります）。",
                    )}
                  </Text>
                )}
                {/* 🔴 件数は真値（*_count）で出す。配列は表示用に先頭 N 件へ
                    切り詰めてあり、.length だと 500 行の失敗が「20 件」に見えた
                    まま取り込みを確定してしまう。 */}
                {(pendingResult.unmatched_count ??
                  pendingResult.unmatched_skus.length) > 0 && (
                  <Text as="p">
                    {t(
                      "一致する商品が見つからない SKU: {n} 件（この行は取り込まれません）。",
                      {
                        n:
                          pendingResult.unmatched_count ??
                          pendingResult.unmatched_skus.length,
                      },
                    )}
                  </Text>
                )}
                {(pendingResult.invalid_row_count ??
                  pendingResult.invalid_rows.length) > 0 && (
                  <Text as="p">
                    {t("読み飛ばす行: {n} 件。", {
                      n:
                        pendingResult.invalid_row_count ??
                        pendingResult.invalid_rows.length,
                    })}
                  </Text>
                )}
                {pendingResult.ambiguous_sku_count > 0 && (
                  <Text as="p">
                    {t(
                      "同じ SKU の商品が複数あるため取り込まない行: {n} 件。",
                      {
                        n: pendingResult.ambiguous_sku_count,
                      },
                    )}
                  </Text>
                )}
                {pendingResult.unmatched_supplier_count > 0 && (
                  <Text as="p">
                    {t(
                      "一致する仕入先が見つからない Vendor: {n} 件（この行は取り込まれません）。",
                      {
                        n: pendingResult.unmatched_supplier_count,
                      },
                    )}
                  </Text>
                )}
                {pendingResult.ambiguous_supplier_count > 0 && (
                  <Text as="p">
                    {t(
                      "同じ名前の仕入先が複数ある Vendor: {n} 件（この行は取り込まれません）。",
                      {
                        n: pendingResult.ambiguous_supplier_count,
                      },
                    )}
                  </Text>
                )}
              </BlockStack>
            </Banner>
          )}

          {applyMutation.isError && (
            <Banner tone="critical" title={t("取り込みに失敗しました")}>
              <p>{(applyMutation.error as Error).message}</p>
            </Banner>
          )}

          {result?.normalization_mode === "retention_only" && (
            <Banner
              tone="warning"
              title={t(
                "原本と汎用JSONLを保存しました（発注点・リードタイムは未反映）",
              )}
            >
              <Text as="p">
                {t(
                  "{n}行の正確な原本と解析可能な全行を汎用JSONLとして保存しました。発注点・リードタイムには1件も書き込んでいません。保存済みデータの取り出しから汎用JSONLを分割取得できます。",
                  {
                    n: result.total_rows,
                  },
                )}
              </Text>
            </Banner>
          )}

          {result && result.normalization_mode !== "retention_only" && (
            <Banner
              tone={writtenCount(result) > 0 ? "success" : "warning"}
              title={t(
                "発注点を新規 {created} 件・更新 {updated} 件、仕入先別リードタイムを新規 {leadCreated} 件・更新 {leadUpdated} 件取り込みました",
                {
                  created: result.created,
                  updated: result.updated,
                  leadCreated: result.lead_times_created,
                  leadUpdated: result.lead_times_updated,
                },
              )}
            >
              <BlockStack gap="200">
                <Text as="p">
                  {t("一致した商品 {matched} 件", {
                    matched: result.matched_variants,
                  })}
                </Text>
                <Text as="p">
                  {t(
                    "変更なし: 発注点 {unchanged} 件・リードタイム {leadUnchanged} 件 / 既定仕入先の整合修復 {preferredRepaired} 件",
                    {
                      unchanged: result.unchanged,
                      leadUnchanged: result.lead_times_unchanged,
                      preferredRepaired: result.preferred_repaired,
                    },
                  )}
                </Text>
                {result.created + result.updated > 0 && (
                  <Text as="p">
                    {t(
                      "取り込んだ数値は「発注点」そのものです。実際にアラートが鳴るラインは、ここにサービスレベル（統計的な安全在庫）と季節係数を加えた値になるため、Stocky と同じ数字を入れても少し大きくなります。内訳は発注点設定の各行で確認できます。",
                    )}
                  </Text>
                )}
                {(result.unmatched_count ?? result.unmatched_skus.length) >
                  0 && (
                  <Text as="p">
                    {t("一致する商品が見つからなかった SKU（{n} 件）: {skus}", {
                      n: result.unmatched_count ?? result.unmatched_skus.length,
                      skus: result.unmatched_skus.slice(0, 10).join(", "),
                    })}
                    {(result.unmatched_count ?? result.unmatched_skus.length) >
                    10
                      ? " …"
                      : ""}
                    {t("。先に在庫同期を実行してから再取り込みしてください。")}
                  </Text>
                )}
                {result.ambiguous_sku_count > 0 && (
                  <Text as="p">
                    {t(
                      "同じ SKU の商品が複数あり特定できなかった行（{n} 件）: {values}",
                      {
                        n: result.ambiguous_sku_count,
                        values: listed(result.ambiguous_skus),
                      },
                    )}
                  </Text>
                )}
                {result.unmatched_supplier_count > 0 && (
                  <Text as="p">
                    {t(
                      "一致する仕入先が見つからなかった Vendor（{n} 件）: {values}",
                      {
                        n: result.unmatched_supplier_count,
                        values: listed(result.unmatched_suppliers),
                      },
                    )}
                  </Text>
                )}
                {result.ambiguous_supplier_count > 0 && (
                  <Text as="p">
                    {t(
                      "同じ名前の仕入先が複数あり特定できなかった Vendor（{n} 件）: {values}",
                      {
                        n: result.ambiguous_supplier_count,
                        values: listed(result.ambiguous_suppliers),
                      },
                    )}
                  </Text>
                )}
                {result.invalid_rows.length > 0 && (
                  <Text as="p">
                    {t("読み飛ばした行（{n} 件）:", {
                      n: result.invalid_row_count ?? result.invalid_rows.length,
                    })}{" "}
                    {result.invalid_rows
                      .map((row) =>
                        t("{row}行目（{reason}）", {
                          row: row.row,
                          reason: row.reason,
                        }),
                      )
                      .join(" / ")}
                  </Text>
                )}
              </BlockStack>
            </Banner>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
